import { defineNuxtPlugin } from '#app';
import { nextTick } from 'vue';
import { useScrollHistory } from '~/composables/useScrollHistory';

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.hook('app:mounted', () => {
    const {
      navigateToSection,
      recordSection,
      restoreFromState,
      startPassiveScrollTracking,
      clearScrollState,
      scrollToTop,
    } = useScrollHistory();
    const router = nuxtApp.$router;

    nuxtApp.provide('recordSection', recordSection);
    nuxtApp.provide('navigateToSection', navigateToSection);

    // Track navigation type
    let isPopStateNavigation = false;
    let navigationType = 'unknown';
    let pendingRestoreState = null;

    // Detect navigation type on popstate
    window.addEventListener('popstate', (event) => {
      isPopStateNavigation = true;
      navigationType = 'popstate';
      pendingRestoreState = event.state; // Save state for later
    });

    router.beforeEach((to, from, next) => {
      if (!isPopStateNavigation) {
        navigationType = 'forward';
      }
      next();
    });

    router.afterEach((to, from) => {
      // Handle popstate restoration AFTER page is rendered
      if (isPopStateNavigation && pendingRestoreState) {
        // Wait for Vue to finish rendering
        nextTick(() => {
          // Then wait a bit more for images/content to load
          setTimeout(() => {
            restoreFromState(pendingRestoreState);

            // Reset flags
            setTimeout(() => {
              isPopStateNavigation = false;
              navigationType = 'unknown';
              pendingRestoreState = null;
            }, 300);
          }, 300); // Give content time to render
        });

        // Don't do the normal scroll tracking setup yet
        return;
      }

      // For forward navigation, clear scroll state and scroll to top
      if (navigationType === 'forward') {
        clearScrollState();

        nextTick(() => {
          setTimeout(() => {
            scrollToTop();
          }, 0);
        });
      }

      // Set up scroll tracking for the new page
      setTimeout(() => {
        const sections = document.querySelectorAll('section[id]');

        if (sections.length) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  recordSection(entry.target.id);
                }
              });
            },
            { threshold: 0.5 }
          );
          sections.forEach((section) => observer.observe(section));
        } else {
          startPassiveScrollTracking();
        }
      }, 400); // Wait longer before starting tracking
    });

    // Only restore on initial hard page load if there's actual state
    const initialState = history.state;
    if (initialState?.section || initialState?.scrollY != null) {
      setTimeout(() => restoreFromState(initialState), 300);
    }
  });
});
